/** Pure status derivation for the user-management list view. */

export type UserStatus = 'active' | 'not_invited' | 'invited' | 'invite_expired' | 'deleted';

export type InvitationStatus = 'pending' | 'accepted' | null;

/** Which welcome template a user was last sent. */
export type WelcomeEmailKind = 'set_up' | 'sso_notice' | null;

export interface UserStatusInputs {
  /** ISO string / Date when the user was soft-deleted; null when active. */
  deleted: string | Date | null | undefined;
  /** Pending / accepted / null (pre-existing) / undefined (legacy doc). */
  invitation_status: InvitationStatus | undefined;
  /**
   * When a welcome email was last SUCCESSFULLY sent, or null if never.
   * Recorded by the welcome-email service after the send — not to be confused
   * with `invitation_sent_at`, which is stamped at create time for every
   * non-SSO user whether or not any mail went out.
   */
  welcome_email_sent_at?: string | Date | null;
  /** Which template that send used; `sso_notice` carries no expiring link. */
  welcome_email_kind?: WelcomeEmailKind;
  /** Invitation lifetime in days. */
  invitation_expiry_days?: number;
}

export const DEFAULT_INVITATION_EXPIRY_DAYS = 14;

const MS_PER_DAY = 86_400_000;

const toDate = (value: string | Date): Date => (value instanceof Date ? value : new Date(value));

/**
 * Derives the list-view status.
 *
 * Reads the recorded send rather than inferring one. Previously the absence of
 * `invitation_sent_at` was taken to mean "SSO user", which conflated two very
 * different people: someone whose domain federates sign-in, and someone created
 * with "don't send an invitation" who was then shown as *Invited* — a state
 * they had never been put in. Those now separate cleanly:
 *
 * - never sent anything          → `not_invited`
 * - sent an SSO notice           → `invited`, no expiry (their IdP signs them in)
 * - sent a set-up link, in date  → `invited`
 * - sent a set-up link, lapsed   → `invite_expired`
 */
export const deriveUserStatus = (inputs: UserStatusInputs, now: Date = new Date()): UserStatus => {
  if (inputs.deleted) return 'deleted';
  if (inputs.invitation_status !== 'pending') return 'active';

  if (!inputs.welcome_email_sent_at) {
    // An SSO user can be legitimately pending with nothing sent — their invite
    // is the IdP itself, so treat a recorded SSO kind as invited regardless.
    return inputs.welcome_email_kind === 'sso_notice' ? 'invited' : 'not_invited';
  }

  // An SSO notice carries no set-up link, so it has nothing to expire.
  if (inputs.welcome_email_kind === 'sso_notice') return 'invited';

  const sentAt: Date = toDate(inputs.welcome_email_sent_at);
  const expiryDays: number = inputs.invitation_expiry_days ?? DEFAULT_INVITATION_EXPIRY_DAYS;
  const expiresAtMs: number = sentAt.getTime() + expiryDays * MS_PER_DAY;
  return now.getTime() < expiresAtMs ? 'invited' : 'invite_expired';
};
